import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { useApp } from "@/contexts/AppContext";

/**
 * Floating WhatsApp support button.
 * Phone number is read from REACT_APP_WHATSAPP_NUMBER (build time).
 * Pre-fills a message that includes selected scenarios (if any).
 */
export default function WhatsAppButton() {
  const { lang, selectedScenarios, scenarios, requiredProducts, localized } = useApp();
  const [open, setOpen] = useState(false);

  const rawNumber = process.env.REACT_APP_WHATSAPP_NUMBER || "905551234567";
  const number = rawNumber.replace(/[^0-9]/g, "");

  const labels = {
    tr: {
      open: "WhatsApp destek",
      title: "Bize WhatsApp'tan yazın",
      subtitle: "Genelde dakikalar içinde yanıtlıyoruz.",
      withScenes: "Seçili senaryolarınızla birlikte gönderelim mi?",
      sendGeneric: "Genel mesaj gönder",
      sendWithScenes: "Seçimlerimle birlikte gönder",
      defaultMsg:
        "Merhaba, akıllı ev projem için destek almak istiyorum.",
      scenesPrefix: "Şu senaryolarla ilgileniyorum:",
      productsPrefix: "İhtiyacım olabilecek donanımlar:",
    },
    en: {
      open: "WhatsApp support",
      title: "Message us on WhatsApp",
      subtitle: "We usually reply within minutes.",
      withScenes: "Send with your selected scenarios?",
      sendGeneric: "Send a generic message",
      sendWithScenes: "Send with my selections",
      defaultMsg: "Hi, I'd like help with my smart home project.",
      scenesPrefix: "I'm interested in these scenarios:",
      productsPrefix: "Hardware I might need:",
    },
  };
  const L = labels[lang] || labels.tr;

  const buildLink = (withSelections) => {
    let msg = L.defaultMsg;
    if (withSelections && selectedScenarios.length > 0) {
      const lines = [L.defaultMsg, "", L.scenesPrefix];
      selectedScenarios.forEach((slug) => {
        const sc = scenarios.find((x) => x.slug === slug);
        if (sc) lines.push(`• ${localized(sc, "title")}`);
      });
      if (requiredProducts.length > 0) {
        lines.push("", L.productsPrefix);
        requiredProducts.forEach((p) => lines.push(`• ${localized(p, "title")}`));
      }
      msg = lines.join("\n");
    }
    return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  };

  const hasSelections = selectedScenarios.length > 0;

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="whatsapp-fab"
        aria-label={L.open}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 left-5 sm:bottom-6 sm:left-6 z-[60] h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-[0_10px_30px_rgba(37,211,102,0.45)] flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="h-6 w-6" strokeWidth={2.4} />
            </motion.span>
          ) : (
            <motion.span
              key="open"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <WhatsAppGlyph />
            </motion.span>
          )}
        </AnimatePresence>
        {/* pulse ring */}
        {!open && (
          <span className="absolute inset-0 rounded-full ring-2 ring-[#25D366]/40 animate-ping pointer-events-none" />
        )}
      </button>

      {/* Popover */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 left-5 sm:left-6 z-[60] w-[300px] rounded-2xl border border-white/[0.08] bg-[#0B0F19] shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden"
          >
            <div className="bg-[#0e3b2a] border-b border-white/5 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#25D366] flex items-center justify-center">
                  <WhatsAppGlyph small />
                </div>
                <div>
                  <div className="font-heading text-white text-sm font-medium">
                    {L.title}
                  </div>
                  <div className="text-xs text-emerald-200/80 mt-0.5">
                    {L.subtitle}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-2">
              {hasSelections && (
                <>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-1 font-bold">
                    {L.withScenes}
                  </div>
                  <a
                    data-testid="whatsapp-send-with-selection"
                    href={buildLink(true)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center text-sm font-semibold bg-[#25D366] hover:bg-[#1ebe5d] text-black rounded-xl px-4 py-3 transition-colors"
                  >
                    {L.sendWithScenes} ({selectedScenarios.length})
                  </a>
                </>
              )}
              <a
                data-testid="whatsapp-send-generic"
                href={buildLink(false)}
                target="_blank"
                rel="noopener noreferrer"
                className={`block w-full text-center text-sm rounded-xl px-4 py-3 transition-colors ${
                  hasSelections
                    ? "border border-white/15 text-gray-200 hover:bg-white/5"
                    : "bg-[#25D366] hover:bg-[#1ebe5d] text-black font-semibold"
                }`}
              >
                {L.sendGeneric}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function WhatsAppGlyph({ small = false }) {
  const size = small ? 16 : 24;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
    </svg>
  );
}
